import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import {
  ArrowLeft,
  Building2,
  User2,
  Plus,
  Users,
  PackageSearch,
  BarChart3,
} from "lucide-react";
import { motion } from "framer-motion";
import QRCode from "react-qr-code";
import CreateUserModal from "../../components/modals/CreateUserModal";
import EditUserModal from "../../components/modals/EditUserModal";
import ResetPasswordModal from "../../components/modals/ResetPasswordModal";
import AssignOwner from "../../components/AssignOwner";
import { getImageUrl } from "../../utils/getImageUrl";

const KpiCard = ({ title, value, icon }) => (
  <motion.div
    whileHover={{ scale: 1.03 }}
    className="bg-white/80 backdrop-blur-md p-5 rounded-2xl shadow-md flex items-center gap-4"
  >
    <div className="text-supernova-500">{icon}</div>
    <div>
      <h4 className="text-gray-600 text-sm">{title}</h4>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  </motion.div>
);

export default function CompanyProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_URL;

  const [company, setCompany] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  const openEditUser = (user) => setEditUser(user);
  const openResetPassword = (user) => setResetUser(user);

  const closeModals = () => {
    setEditUser(null);
    setResetUser(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resCompany, resMetrics, resProducts, resUsers] =
          await Promise.all([
            API.get(`/companies/by-id/${id}`),
            API.get(`/metrics/company/${id}`),
            API.get(`/products/company/${id}`),
            API.get(`/companies/${id}/users`),
          ]);

        setCompany(resCompany.data?.data?.company ?? resCompany.data?.data ?? {});
        setMetrics(resMetrics.data?.data ?? {});
        setProducts(resProducts.data?.data?.products ?? resProducts.data?.data?.data ?? []);
        setUsers(resUsers.data?.data?.users ?? resUsers.data?.data?.data ?? []);
      } catch (err) {
        console.log(err);
        toast.error("Error cargando información de la empresa");
      }
    };

    fetchData();
  }, [id]);

  const reloadUsers = async () => {
    try {
      const res = await API.get(`/companies/${id}/users`);
      setUsers(res.data?.data?.users ?? res.data?.data?.data ?? []);
    } catch {
      toast.error("Error al actualizar usuarios");
    }
  };

  if (!company || !metrics)
    return <p className="text-center mt-10 text-gray-500">Cargando...</p>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-supernova-50 to-supernova-100 p-6 md:p-10">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-supernova-600 transition mb-6"
        >
          <ArrowLeft size={18} /> Volver
        </button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/80 backdrop-blur-md rounded-2xl shadow p-6 flex flex-col sm:flex-row items-center sm:items-start gap-6 mb-10"
        >
          <div className="bg-supernova-100 text-supernova-600 rounded-full p-3 w-28 h-28 flex items-center justify-center overflow-hidden">
            {company.logo ? (
              <img
                src={getImageUrl(company.logo)}
                alt="Logo"
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <Building2 size={40} />
            )}
          </div>

          <div className="text-center sm:text-left">
            <h1 className="text-3xl font-bold text-gray-800">{company.name}</h1>
            <p className="text-sm text-gray-500">@{company.slug}</p>
            <p className="text-gray-600 mt-2">{company.description}</p>
            <span className="inline-block text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg mt-3">
              {company.category?.name || "Sin categoría"}
            </span>
          </div>
        </motion.div>

        <div className="bg-white/80 backdrop-blur-md p-6 rounded-2xl shadow mb-10">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <User2 className="text-supernova-500" />
            Admin de la empresa
          </h2>

          {company.owner ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-supernova-100 text-supernova-600 rounded-full p-2">
                  <User2 />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">
                    {company.owner.name}
                  </p>
                  <p className="text-sm text-gray-500">{company.owner.email}</p>
                </div>
              </div>

              <span className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">
                Admin asignado
              </span>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl">
              <p className="text-yellow-800 font-medium mb-3">
                ⚠ Esta empresa no tiene admin asignado
              </p>

              <AssignOwner
                companyId={company.id}
                users={users}
                onAssigned={(updatedCompany) => {
                  setCompany(updatedCompany);
                }}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
          <KpiCard title="Usuarios" value={users.length} icon={<Users />} />
          <KpiCard
            title="Productos"
            value={products.length}
            icon={<PackageSearch />}
          />
          <KpiCard
            title="Visitas"
            value={metrics.totalVisits}
            icon={<BarChart3 />}
          />
        </div>

        <div className="bg-white/80 backdrop-blur-xl p-6 rounded-2xl shadow-md mb-10 flex flex-col items-center">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">
            Perfil público con catálogo
          </h3>

          <QRCode
            value={`${API_URL}company/${company.id}`}
            style={{ height: "180px", width: "180px" }}
            bgColor="#FFFFFF"
            fgColor="#000000"
            level="H"
          />

          <p className="text-gray-600 text-sm mt-3">
            Escanea para ver el catálogo virtual
          </p>
        </div>

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-700">
            Usuarios de la empresa
          </h2>
          <button
            onClick={() => setShowModal(true)}
            className="bg-supernova-500 hover:bg-supernova-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 transition"
          >
            <Plus size={16} /> Nuevo Usuario
          </button>
        </div>

        {users.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-10">
            {users.map((u, i) => (
              <motion.div
                key={u.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white p-4 rounded-xl shadow hover:shadow-md transition flex justify-between items-center"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-supernova-100 text-supernova-600 rounded-full p-2">
                    <User2 size={22} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{u.name}</p>
                    <p className="text-sm text-gray-500">{u.email}</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => openEditUser(u)}
                    className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-lg"
                  >
                    Editar
                  </button>

                  <button
                    onClick={() => openResetPassword(u)}
                    className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded-lg"
                  >
                    Reset clave
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No hay usuarios asignados.</p>
        )}

        {showModal && (
          <CreateUserModal
            companyId={id}
            onClose={() => setShowModal(false)}
            onUserCreated={reloadUsers}
          />
        )}
        {editUser && (
          <EditUserModal
            user={editUser}
            onClose={closeModals}
            onUpdated={reloadUsers}
          />
        )}

        {resetUser && (
          <ResetPasswordModal user={resetUser} onClose={closeModals} />
        )}
      </div>
    </div>
  );
}
